'use client';

import React from 'react';
import { useLanguage } from '@/context/LanguageContext';

type Section = { h: string; p: string[] };
type Doc = { title: string; updated: string; intro: string; sections: Section[] };

const CONTENT: Record<'es' | 'en', Doc> = {
  es: {
    title: 'Términos y Condiciones',
    updated: 'Última actualización: 15 de junio de 2026',
    intro:
      'Estos Términos y Condiciones regulan el uso del sitio web mercaliacg.com, operado por MERCALIA Connect Group LLC. Al acceder al sitio, usted acepta estos términos.',
    sections: [
      {
        h: '1. Uso del sitio',
        p: [
          'El contenido de este sitio se ofrece con fines informativos sobre nuestros servicios de abastecimiento, consultoría y comercio de materias primas. Usted se compromete a usar el sitio de forma lícita y a no afectar su funcionamiento.',
        ],
      },
      {
        h: '2. No es una oferta vinculante',
        p: [
          'La información sobre productos, materias primas y capacidades no constituye una oferta comercial vinculante. Toda operación queda sujeta a un contrato escrito independiente, a disponibilidad y a las verificaciones de cumplimiento aplicables.',
        ],
      },
      {
        h: '3. Propiedad intelectual',
        p: [
          'Las marcas, logotipos, textos y diseños de este sitio son propiedad de MERCALIA Connect Group LLC y no pueden reproducirse sin autorización previa por escrito.',
        ],
      },
      {
        h: '4. Limitación de responsabilidad',
        p: [
          'El sitio se proporciona "tal cual". En la medida permitida por la ley, MERCALIA no será responsable de daños derivados del uso o la imposibilidad de uso del sitio o de la información en él contenida.',
        ],
      },
      {
        h: '5. Enlaces de terceros',
        p: [
          'El sitio puede contener enlaces a sitios de terceros. No controlamos ni respondemos por su contenido o sus prácticas de privacidad.',
        ],
      },
      {
        h: '6. Ley aplicable',
        p: [
          'Estos términos se rigen por las leyes del Estado de Nuevo México, EE. UU., sin perjuicio de las normas imperativas de protección al consumidor que pudieran aplicarse.',
        ],
      },
      {
        h: '7. Contacto',
        p: ['Para consultas sobre estos términos: contact@mercaliacg.com.'],
      },
    ],
  },
  en: {
    title: 'Terms & Conditions',
    updated: 'Last updated: June 15, 2026',
    intro:
      'These Terms & Conditions govern the use of the website mercaliacg.com, operated by MERCALIA Connect Group LLC. By accessing the site, you agree to these terms.',
    sections: [
      {
        h: '1. Use of the site',
        p: [
          'The content of this site is provided for informational purposes about our commodity sourcing, consulting, and trading services. You agree to use the site lawfully and not to disrupt its operation.',
        ],
      },
      {
        h: '2. Not a binding offer',
        p: [
          'Information about products, commodities, and capabilities does not constitute a binding commercial offer. Any transaction is subject to a separate written contract, availability, and applicable compliance checks.',
        ],
      },
      {
        h: '3. Intellectual property',
        p: [
          'The trademarks, logos, text, and designs on this site are the property of MERCALIA Connect Group LLC and may not be reproduced without prior written authorization.',
        ],
      },
      {
        h: '4. Limitation of liability',
        p: [
          'The site is provided "as is". To the extent permitted by law, MERCALIA shall not be liable for damages arising from the use or inability to use the site or the information it contains.',
        ],
      },
      {
        h: '5. Third-party links',
        p: [
          'The site may contain links to third-party sites. We do not control or take responsibility for their content or privacy practices.',
        ],
      },
      {
        h: '6. Governing law',
        p: [
          'These terms are governed by the laws of the State of New Mexico, USA, without prejudice to any mandatory consumer-protection rules that may apply.',
        ],
      },
      {
        h: '7. Contact',
        p: ['For questions about these terms: contact@mercaliacg.com.'],
      },
    ],
  },
};

export default function TermsContent() {
  const { lang } = useLanguage();
  const doc = CONTENT[lang] ?? CONTENT.en;

  return (
    <section className="relative pt-36 pb-24 px-6">
      <div className="max-w-3xl mx-auto">
        <h1
          className="font-display font-bold text-primary mb-3"
          style={{ fontSize: 'clamp(36px, 5vw, 56px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}
        >
          {doc.title}
        </h1>
        <p className="text-sm text-brand-muted mb-10">{doc.updated}</p>
        <p className="text-brand-muted leading-relaxed mb-10">{doc.intro}</p>

        <div className="flex flex-col gap-8">
          {doc.sections.map((s) => (
            <div key={s.h}>
              <h2 className="font-display text-xl font-bold text-primary mb-3">{s.h}</h2>
              {s.p.map((para, i) => (
                <p key={i} className="text-brand-muted leading-relaxed mb-2">
                  {para}
                </p>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
